// split-paychecks — Stewardship pay-cadence rounding rule.
//
// Per Baker's 2026-04-24 confirmation: when a monthly commission
// total is split across N paychecks, the FIRST paycheck rounds DOWN
// and the SECOND rounds UP (and so on, alternating) so the sum of
// paychecks always equals the original total exactly. Half-cent
// rounding is the typical case; whole-cent rounding when the total
// divides evenly is a no-op.
//
// All amounts in integer cents. No floats.
//
// Examples (from Baker's 2026-04-24 review):
//   $19,717.57 split into 2 paychecks
//     → Paycheck 1 (May 15, round down): $9,858.78
//     → Paycheck 2 (May 31, round up):   $9,858.79
//     Sum = $19,717.57 ✓
//
//   $20,793 (whole-dollar, splits evenly into halves of $10,396.50)
//     → Paycheck 1 (round down): $10,396
//     → Paycheck 2 (round up):   $10,397
//     Sum = $20,793 ✓
//
//   $0  (zero or negative — produces zero-amount paychecks)
//     → All paychecks: $0
//
//   Negative income (refunds exceeded commissions, edge case)
//     → Floored to $0 per advisor — no claw-back. Per Baker:
//       "run as normal with negative income and then $0 for
//       commissions and $0's for paycheck split."

export interface SplitPaychecksResult {
  paychecks_cents: number[];           // length = paycheck_count
  total_cents: number;                 // input total, mirrored back for callers
  rounding_per_paycheck: Array<"down" | "up" | "exact">;
}

/**
 * Split a monthly commission total across N paychecks using
 * Stewardship's rounding rule (first down, second up, alternating).
 *
 * Returns one cent value per paycheck. Negative inputs floor to all
 * zeros.
 */
export function splitPaychecks(
  total_cents: number,
  paycheck_count: number,
): SplitPaychecksResult {
  if (paycheck_count <= 0) {
    return {
      paychecks_cents: [],
      total_cents,
      rounding_per_paycheck: [],
    };
  }

  // Negative-income edge case (chargebacks > new commissions):
  // produce zero paychecks per Baker's rule. Don't claw back.
  if (total_cents <= 0) {
    return {
      paychecks_cents: new Array(paycheck_count).fill(0),
      total_cents,
      rounding_per_paycheck: new Array(paycheck_count).fill("exact"),
    };
  }

  if (paycheck_count === 1) {
    return {
      paychecks_cents: [total_cents],
      total_cents,
      rounding_per_paycheck: ["exact"],
    };
  }

  // Even-divisor short-circuit — no rounding tension.
  if (total_cents % paycheck_count === 0) {
    const each = total_cents / paycheck_count;
    return {
      paychecks_cents: new Array(paycheck_count).fill(each),
      total_cents,
      rounding_per_paycheck: new Array(paycheck_count).fill("exact"),
    };
  }

  // Uneven case — first paychecks get the floor, last paycheck gets
  // the ceiling so the sum equals total_cents.
  // For paycheck_count > 2, distribute the remainder onto the LATER
  // paychecks (so first → down, last → up). With paycheck_count = 2
  // this matches Baker's rule exactly.
  const baseCents = Math.floor(total_cents / paycheck_count);
  const remainder = total_cents - baseCents * paycheck_count; // < paycheck_count
  const result: number[] = [];
  const rounding: Array<"down" | "up" | "exact"> = [];
  for (let i = 0; i < paycheck_count; i += 1) {
    // Paycheck index from the END gets the +1 cents (round up).
    // Paychecks before that get baseCents (round down).
    const fromEnd = paycheck_count - 1 - i;
    if (fromEnd < remainder) {
      result.push(baseCents + 1);
      rounding.push("up");
    } else {
      result.push(baseCents);
      rounding.push("down");
    }
  }
  return {
    paychecks_cents: result,
    total_cents,
    rounding_per_paycheck: rounding,
  };
}

/**
 * Labels for the two paychecks a monthly commission is paid across:
 * `["Paycheck 1 (August 15)", "Paycheck 2 (August 31)"]`.
 *
 * @param period  the month the commission covers, "YYYY-MM"
 *
 * Paychecks land the month AFTER the income month, because the run
 * happens on the 7th of the following month for prior-month income —
 * so April 2026 commissions pay out May 15 + May 31. The last day is
 * computed rather than assumed, so 30-day months and February come out
 * right.
 *
 * DECISION: this lives beside the split math rather than in each
 * runner. It was two byte-identical private copies (insurance +
 * financial), and the Commission Worksheet needed a third — the point
 * at which a copied helper starts drifting. The amounts and the dates
 * they're paid on are one answer to one question ("what does the
 * advisor actually get, and when"), so they belong in one module.
 */
export function buildPaycheckLabels(period: string): string[] {
  const [yyyy, mm] = period.split("-").map((s) => parseInt(s, 10));
  if (!Number.isFinite(yyyy) || !Number.isFinite(mm) || mm! < 1 || mm! > 12) {
    // Unparseable period — fall back to unnumbered labels rather than
    // printing "Paycheck 1 (Invalid Date)" on a payroll document.
    return ["Paycheck 1", "Paycheck 2"];
  }
  const nextMonthFirst = new Date(Date.UTC(yyyy!, mm!, 1));
  const monthName = nextMonthFirst.toLocaleString("en-US", {
    month: "long",
    timeZone: "UTC",
  });
  const lastDayOfNext = new Date(Date.UTC(yyyy!, mm! + 1, 0)).getUTCDate();
  return [
    `Paycheck 1 (${monthName} 15)`,
    `Paycheck 2 (${monthName} ${lastDayOfNext})`,
  ];
}
